import { supabase } from '../supabaseClient';
import { AnnualTimetableCell, AnnualTimetableCellUpsert } from '../types/domain';
import {
  applyAnnualTimetableSaveQueryUpdates,
  mergeAnnualTimetableCellsQueryData,
  mergeAnnualProgressTimetableCellsBulkQueryData,
  type AnnualTimetableSaveScope,
} from '../query/cacheUpdates';
import { canWriteSchoolScopedDataCache, notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import { learningContentScopeVersionFromHead, buildLearningContentScopeVersion } from '../utils/learningContentCacheVersion';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

const ANNUAL_TIMETABLE_SELECT =
  'id, workspace_id, grade_id, academic_year, term_number, monday_iso, weekday_key, slot_index, text_value, memo, color_hex, slot_kind, is_editable, is_dedicated, is_base_excluded, created_at, updated_at';
/** v3: contentVersion(head) 비교용 메타 */
const ANNUAL_TIMETABLE_CACHE_VERSION = 'v3';

const ANNUAL_TIMETABLE_PAGE_SIZE = 1000;

type AnnualTimetableCachePayload = {
  rows: AnnualTimetableCell[];
  savedAt: number;
  contentVersion?: string;
};

function cacheKey(workspaceId: string, academicYear: number, termNumber: 1 | 2, gradeId: string): string {
  return `cp.annualTimetableCells.${ANNUAL_TIMETABLE_CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

/** 이 워크스페이스의 연간시간표 셀 LS를 학년·학기 키에서 모은다. 네트워크 없음. */
export function listCachedAnnualTimetableCells(
  workspaceId: string,
  academicYear?: number
): AnnualTimetableCell[] {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return [];
  const prefix = `cp.annualTimetableCells.${ANNUAL_TIMETABLE_CACHE_VERSION}.${sid}.`;
  const rows: AnnualTimetableCell[] = [];
  const seen = new Set<string>();
  try {
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const key = deviceCacheStorage.key(i);
      if (!key?.startsWith(prefix)) continue;
      const matched = /^(\d+)\.(1|2)\.(.+)$/.exec(key.slice(prefix.length));
      if (!matched) continue;
      const year = Number(matched[1]);
      const termNumber = Number(matched[2]) as 1 | 2;
      if (academicYear && year !== academicYear) continue;
      const gradeId = matched[3];
      const cacheId = `${year}|${termNumber}|${gradeId}`;
      if (seen.has(cacheId)) continue;
      seen.add(cacheId);
      const hit = loadGradeCache(sid, year, termNumber, gradeId);
      if (hit.found) rows.push(...hit.rows);
    }
  } catch {
    return rows;
  }
  return rows;
}

/** `cp.annualTimetableCells.v2.*` — 대시보드·연간진도 LS fallback용 동기 읽기 */
export function loadAnnualTimetableGradeCellsLocalCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: AnnualTimetableCell[] } {
  return loadGradeCache(workspaceId, academicYear, termNumber, gradeId);
}

/**
 * 학년도·학기 키가 어긋나도 해당 grade의 연간시간표 LS를 찾는다.
 * (메인 프레젠 cacheOnly: meta 학년도와 셀 LS 키가 다를 때)
 */
export function findAnnualTimetableCellsLocalCacheForGrade(
  workspaceId: string,
  gradeId: string,
  preferred?: { academicYear: number; termNumber: 1 | 2 }
): { found: boolean; rows: AnnualTimetableCell[]; academicYear: number; termNumber: 1 | 2 } {
  const sid = workspaceId.trim();
  const gid = gradeId.trim();
  const empty = {
    found: false as const,
    rows: [] as AnnualTimetableCell[],
    academicYear: preferred?.academicYear ?? new Date().getFullYear(),
    termNumber: (preferred?.termNumber ?? 1) as 1 | 2,
  };
  if (!sid || !gid || typeof window === 'undefined') return empty;

  if (preferred) {
    const hit = loadGradeCache(sid, preferred.academicYear, preferred.termNumber, gid);
    if (hit.found && hit.rows.length > 0) {
      return {
        found: true,
        rows: hit.rows,
        academicYear: preferred.academicYear,
        termNumber: preferred.termNumber,
      };
    }
    const otherTerm: 1 | 2 = preferred.termNumber === 1 ? 2 : 1;
    const hitOther = loadGradeCache(sid, preferred.academicYear, otherTerm, gid);
    if (hitOther.found && hitOther.rows.length > 0) {
      return {
        found: true,
        rows: hitOther.rows,
        academicYear: preferred.academicYear,
        termNumber: otherTerm,
      };
    }
  }

  const prefix = `cp.annualTimetableCells.${ANNUAL_TIMETABLE_CACHE_VERSION}.${sid}.`;
  const suffix = `.${gid}`;
  let best: {
    rows: AnnualTimetableCell[];
    academicYear: number;
    termNumber: 1 | 2;
    score: number;
  } | null = null;

  try {
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const key = deviceCacheStorage.key(i);
      if (!key?.startsWith(prefix) || !key.endsWith(suffix)) continue;
      const mid = key.slice(prefix.length, key.length - suffix.length);
      const parts = mid.split('.');
      if (parts.length !== 2) continue;
      const academicYear = Number(parts[0]);
      const termNumber = Number(parts[1]) as 1 | 2;
      if (!Number.isFinite(academicYear) || (termNumber !== 1 && termNumber !== 2)) continue;
      const hit = loadGradeCache(sid, academicYear, termNumber, gid);
      if (!hit.found || hit.rows.length === 0) continue;
      let score = hit.rows.length;
      if (preferred) {
        if (academicYear === preferred.academicYear) score += 1_000_000;
        if (termNumber === preferred.termNumber) score += 100_000;
        score -= Math.abs(academicYear - preferred.academicYear) * 1_000;
      }
      if (!best || score > best.score) {
        best = { rows: hit.rows, academicYear, termNumber, score };
      }
    }
  } catch {
    return empty;
  }

  if (!best) return empty;
  return {
    found: true,
    rows: best.rows,
    academicYear: best.academicYear,
    termNumber: best.termNumber,
  };
}

function loadGradeCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: AnnualTimetableCell[]; contentVersion: string | null } {
  if (typeof window === 'undefined') return { found: false, rows: [], contentVersion: null };
  try {
    const raw = deviceCacheStorage.getItem(cacheKey(workspaceId, academicYear, termNumber, gradeId));
    if (!raw) return { found: false, rows: [], contentVersion: null };
    const parsed = JSON.parse(raw) as AnnualTimetableCachePayload | AnnualTimetableCell[];
    if (Array.isArray(parsed)) {
      return {
        found: true,
        rows: parsed as AnnualTimetableCell[],
        contentVersion: buildLearningContentScopeVersion(parsed as AnnualTimetableCell[]),
      };
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.rows)) {
      return { found: false, rows: [], contentVersion: null };
    }
    const stored =
      typeof parsed.contentVersion === 'string' && parsed.contentVersion.trim()
        ? parsed.contentVersion.trim()
        : buildLearningContentScopeVersion(parsed.rows);
    return { found: true, rows: parsed.rows, contentVersion: stored || null };
  } catch {
    return { found: false, rows: [], contentVersion: null };
  }
}

function saveGradeCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: AnnualTimetableCell[],
  contentVersion?: string
): void {
  if (typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const version = (contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows);
    const payload: AnnualTimetableCachePayload = {
      rows,
      savedAt: Date.now(),
      contentVersion: version,
    };
    deviceCacheStorage.setItem(cacheKey(workspaceId, academicYear, termNumber, gradeId), JSON.stringify(payload));
  } catch {
    // ignore write failures
  }
}

/** 학년·학기 셀 head — `count:max(updated_at)` */
export async function fetchAnnualTimetableCellsHead(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<string> {
  if (!workspaceId || !gradeId) return '0:';
  const { data, error, count } = await supabase
    .from('annual_timetable_cells')
    .select('updated_at', { count: 'exact' })
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const totalCount = count ?? 0;
  const maxUpdatedAt =
    totalCount > 0 && data?.[0]
      ? String((data[0] as { updated_at?: string }).updated_at ?? '')
      : '';
  return learningContentScopeVersionFromHead(totalCount, maxUpdatedAt);
}

/** 대시보드 워밍 후 메인 프레젠이 같은 LS를 읽도록 RQ 성공 데이터를 LS에 반영 */
export function saveAnnualTimetableGradeCellsLocalCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: AnnualTimetableCell[]
): void {
  if (!workspaceId.trim() || !gradeId.trim()) return;
  saveGradeCache(workspaceId, academicYear, termNumber, gradeId, rows);
}

function cellNaturalKey(row: Pick<AnnualTimetableCell, 'monday_iso' | 'weekday_key' | 'slot_index'>): string {
  return `${row.monday_iso}|${row.weekday_key}|${row.slot_index}`;
}

/** upsert 응답 행을 학년·학기별 LS 캐시에 병합 */
export function mergeAnnualTimetableCellsIntoGradeLocalCaches(rows: AnnualTimetableCell[]): void {
  if (!rows.length || typeof window === 'undefined') return;

  const groups = new Map<string, AnnualTimetableCell[]>();
  for (const row of rows) {
    const workspaceId = row.workspace_id?.trim();
    const gradeId = row.grade_id?.trim();
    if (!workspaceId || !gradeId || (row.term_number !== 1 && row.term_number !== 2)) continue;
    const groupKey = `${workspaceId}|${row.academic_year}|${row.term_number}|${gradeId}`;
    const list = groups.get(groupKey) ?? [];
    list.push(row);
    groups.set(groupKey, list);
  }

  groups.forEach((savedChunk, groupKey) => {
    const [workspaceId, yearStr, termStr, gradeId] = groupKey.split('|');
    const academicYear = Number(yearStr);
    const termNumber = Number(termStr) as 1 | 2;
    if (!workspaceId || !gradeId || !academicYear) return;

    const existing = loadGradeCache(workspaceId, academicYear, termNumber, gradeId);
    if (!existing.found) {
      saveGradeCache(workspaceId, academicYear, termNumber, gradeId, savedChunk);
      return;
    }

    const merged = new Map<string, AnnualTimetableCell>();
    for (const row of existing.rows) {
      merged.set(cellNaturalKey(row), row);
    }
    for (const row of savedChunk) {
      merged.set(cellNaturalKey(row), row);
    }
    saveGradeCache(workspaceId, academicYear, termNumber, gradeId, Array.from(merged.values()));
  });
}

function parseAnnualTimetableSaveScope(rows: AnnualTimetableCell[]): AnnualTimetableSaveScope | null {
  const workspaceId = rows[0]?.workspace_id?.trim();
  if (!workspaceId) return null;

  const academicYear = rows[0]?.academic_year;
  if (!academicYear) return null;

  const termNumbers = new Set<1 | 2>();
  const gradeIds = new Set<string>();
  for (const row of rows) {
    if (row.term_number === 1 || row.term_number === 2) {
      termNumbers.add(row.term_number);
    }
    const gradeId = row.grade_id?.trim();
    if (gradeId) gradeIds.add(gradeId);
  }

  if (termNumbers.size === 0 || gradeIds.size === 0) return null;

  return {
    workspaceId,
    academicYear,
    termNumbers: Array.from(termNumbers),
    gradeIds: Array.from(gradeIds),
  };
}

function applyAnnualTimetablePostSaveInvalidation(rows: AnnualTimetableCell[]): void {
  if (!rows.length || typeof window === 'undefined') return;

  const scope = parseAnnualTimetableSaveScope(rows);
  if (!scope) return;

  mergeAnnualTimetableCellsIntoGradeLocalCaches(rows);

  for (const termNumber of scope.termNumbers) {
    mergeAnnualProgressTimetableCellsBulkQueryData(
      scope.workspaceId,
      scope.academicYear,
      termNumber,
      scope.gradeIds,
      rows
    );
    for (const gradeId of scope.gradeIds) {
      mergeAnnualTimetableCellsQueryData(scope.workspaceId, scope.academicYear, termNumber, gradeId, rows);
    }
  }

  applyAnnualTimetableSaveQueryUpdates(scope);
}

/** 로그인 직후 등: `cp.annualTimetableCells.v2.{workspaceId}.*` 학년별 LS 전부 제거 */
export function clearAnnualTimetableGradeCellsLocalCaches(workspaceId: string): void {
  if (typeof window === 'undefined' || !workspaceId.trim()) return;
  const prefix = `cp.annualTimetableCells.${ANNUAL_TIMETABLE_CACHE_VERSION}.${workspaceId.trim()}.`;
  const keysToRemove: string[] = [];
  try {
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const key = deviceCacheStorage.key(i);
      if (key?.startsWith(prefix)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => deviceCacheStorage.removeItem(key));
  } catch {
    // ignore localStorage failures
  }
}

/** 로컬 학년별 캐시를 최신 서버 행으로 덮어씀(`needsReload` 중에는 saveGradeCache가 no-op일 수 있음). */
async function fetchFreshAnnualCellsAndRefreshGradeCaches(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): Promise<AnnualTimetableCell[]> {
  if (!gradeIds.length) return [];
  const fetched = await fetchAnnualTimetableRowsForGrades(workspaceId, academicYear, termNumber, gradeIds);
  const fetchedByGrade = new Map<string, AnnualTimetableCell[]>();
  fetched.forEach((row) => {
    const list = fetchedByGrade.get(row.grade_id);
    if (list) list.push(row);
    else fetchedByGrade.set(row.grade_id, [row]);
  });
  gradeIds.forEach((gradeId) => {
    saveGradeCache(workspaceId, academicYear, termNumber, gradeId, fetchedByGrade.get(gradeId) ?? []);
  });
  return fetched;
}

async function fetchAnnualTimetableRowsForGrades(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): Promise<AnnualTimetableCell[]> {
  if (!gradeIds.length) return [];
  const fetched: AnnualTimetableCell[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('annual_timetable_cells')
      .select(ANNUAL_TIMETABLE_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .in('grade_id', gradeIds)
      .order('grade_id', { ascending: true })
      .order('monday_iso', { ascending: true })
      .order('weekday_key', { ascending: true })
      .order('slot_index', { ascending: true })
      .range(offset, offset + ANNUAL_TIMETABLE_PAGE_SIZE - 1);

    if (error) {
      throw new Error(error.message);
    }
    const batch = (data ?? []) as AnnualTimetableCell[];
    fetched.push(...batch);
    if (batch.length < ANNUAL_TIMETABLE_PAGE_SIZE) break;
    offset += ANNUAL_TIMETABLE_PAGE_SIZE;
  }
  return fetched;
}

export type GetAnnualTimetableCellsOptions = {
  /** true면 로컬 LS를 읽지 않고 해당 학년 ID 전부를 서버에서 다시 받는다(연간 저장 직후 연간진도표와 동기화) */
  forceNetwork?: boolean;
};

/** program_progress_cells 자연키와 동일 좌표 — 프로그램 표 «일정» 열의 DB 원본 */
export type AnnualTimetableNaturalKey = {
  gradeId: string;
  mondayIso: string;
  weekdayKey: string;
  slotIndex: number;
};

const ANNUAL_NATURAL_KEY_OR_CHUNK = 18;
const ANNUAL_WEEKDAY_KEYS = new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat']);
const ANNUAL_MONDAY_ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

function trimAnnualKeyPart(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

/**
 * 특정 주(월요일)의 연간 시간표 셀만 조회.
 * - LS에 학기 캐시가 있으면 네트워크 없이 해당 주만 잘라 반환
 * - 없으면 해당 monday_iso만 DB에서 가져와, 기존 LS가 있을 때만 병합(부분 캐시로 전량을 오염시키지 않음)
 */
export async function getAnnualTimetableCellsForMondayIso(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  mondayIso: string,
  options?: { forceNetwork?: boolean }
): Promise<AnnualTimetableCell[]> {
  if (!workspaceId || !gradeId) return [];
  const mon = trimAnnualKeyPart(mondayIso);
  if (!ANNUAL_MONDAY_ISO_RE.test(mon)) return [];

  const cached = loadGradeCache(workspaceId, academicYear, termNumber, gradeId);
  if (!options?.forceNetwork && cached.found) {
    return cached.rows.filter((r) => trimAnnualKeyPart(r.monday_iso) === mon);
  }

  const fetched: AnnualTimetableCell[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('annual_timetable_cells')
      .select(ANNUAL_TIMETABLE_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .eq('monday_iso', mon)
      .order('weekday_key', { ascending: true })
      .order('slot_index', { ascending: true })
      .range(offset, offset + ANNUAL_TIMETABLE_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as AnnualTimetableCell[];
    fetched.push(...batch);
    if (batch.length < ANNUAL_TIMETABLE_PAGE_SIZE) break;
    offset += ANNUAL_TIMETABLE_PAGE_SIZE;
  }

  // 기존 학기 캐시가 있을 때만 해당 주 행을 병합(없으면 부분 캐시를 전량처럼 저장하지 않음)
  if (cached.found) {
    mergeAnnualTimetableCellsIntoGradeLocalCaches(fetched);
  }
  return fetched;
}

/** 현재 프로그램 표 슬롯 좌표에 해당하는 연간 시간표 셀만 조회(학기 전체 select 회피) */
export async function getAnnualTimetableCellsForNaturalKeys(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  keys: AnnualTimetableNaturalKey[]
): Promise<AnnualTimetableCell[]> {
  if (!workspaceId || !gradeId || !keys.length) return [];
  const seen = new Set<string>();
  const unique: AnnualTimetableNaturalKey[] = [];
  keys.forEach((k) => {
    const wk = trimAnnualKeyPart(k.weekdayKey);
    const mon = trimAnnualKeyPart(k.mondayIso);
    if (!ANNUAL_WEEKDAY_KEYS.has(wk) || !ANNUAL_MONDAY_ISO_RE.test(mon)) return;
    if (!Number.isInteger(k.slotIndex)) return;
    const sk = `${k.gradeId}|${mon}|${wk}|${k.slotIndex}`;
    if (seen.has(sk)) return;
    seen.add(sk);
    unique.push({ gradeId, mondayIso: mon, weekdayKey: wk, slotIndex: k.slotIndex });
  });
  const out: AnnualTimetableCell[] = [];
  for (let i = 0; i < unique.length; i += ANNUAL_NATURAL_KEY_OR_CHUNK) {
    const chunk = unique.slice(i, i + ANNUAL_NATURAL_KEY_OR_CHUNK);
    const orFilter = chunk
      .map((k) => `and(monday_iso.eq.${k.mondayIso},weekday_key.eq.${k.weekdayKey},slot_index.eq.${k.slotIndex})`)
      .join(',');
    const { data, error } = await supabase
      .from('annual_timetable_cells')
      .select(ANNUAL_TIMETABLE_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .or(orFilter);
    if (error) throw new Error(error.message);
    out.push(...((data ?? []) as AnnualTimetableCell[]));
  }
  return out;
}

export async function getAnnualTimetableCells(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[],
  options?: GetAnnualTimetableCellsOptions
): Promise<AnnualTimetableCell[]> {
  if (!workspaceId || gradeIds.length === 0) return [];
  const uniqueGradeIds = Array.from(new Set(gradeIds.filter(Boolean)));
  /** 연간 upsert 직후 전역 needsReload 대신 스코프별 캐시 갱신(`applyAnnualTimetableSaveCacheUpdates`)을 사용한다. */
  const bypassLocalGradeCaches =
    Boolean(options?.forceNetwork) || !canWriteSchoolScopedDataCache(workspaceId);
  if (bypassLocalGradeCaches) {
    return fetchFreshAnnualCellsAndRefreshGradeCaches(workspaceId, academicYear, termNumber, uniqueGradeIds);
  }
  const cachedRows: AnnualTimetableCell[] = [];
  const missingGradeIds: string[] = [];
  for (const gradeId of uniqueGradeIds) {
    const cached = loadGradeCache(workspaceId, academicYear, termNumber, gradeId);
    if (cached.found && cached.contentVersion) {
      try {
        const headVersion = await fetchAnnualTimetableCellsHead(
          workspaceId,
          academicYear,
          termNumber,
          gradeId
        );
        if (headVersion === cached.contentVersion) {
          if (process.env.NODE_ENV === 'development') {
            recordDevLocalStorageProgramCacheUse('연간시간표·셀·LS(head 일치)');
          }
          cachedRows.push(...cached.rows);
          continue;
        }
      } catch {
        /* head 실패 시 풀 조회 */
      }
    }
    missingGradeIds.push(gradeId);
  }
  if (missingGradeIds.length === 0) return cachedRows;
  const fetched = await fetchAnnualTimetableRowsForGrades(workspaceId, academicYear, termNumber, missingGradeIds);
  const fetchedByGrade = new Map<string, AnnualTimetableCell[]>();
  fetched.forEach((row) => {
    const list = fetchedByGrade.get(row.grade_id);
    if (list) list.push(row);
    else fetchedByGrade.set(row.grade_id, [row]);
  });
  missingGradeIds.forEach((gradeId) => {
    saveGradeCache(workspaceId, academicYear, termNumber, gradeId, fetchedByGrade.get(gradeId) ?? []);
  });
  return [...cachedRows, ...fetched];
}

export async function upsertAnnualTimetableCells(
  rows: AnnualTimetableCellUpsert[]
): Promise<AnnualTimetableCell[]> {
  if (rows.length === 0) return [];
  const payload = rows.map((row) => {
    const { id: _ignoredId, ...rest } = row;
    const base = {
      ...rest,
      updated_at: new Date().toISOString(),
    };
    // onConflict 키로 upsert하므로 id는 절대 전송하지 않는다.
    // 일부 행만 id를 포함하면 나머지 행의 id가 null로 해석되어 NOT NULL 위반(400)이 날 수 있다.
    return base;
  });

  const { data, error } = await supabase
    .from('annual_timetable_cells')
    .upsert(payload, {
      onConflict: 'workspace_id,grade_id,academic_year,term_number,monday_iso,weekday_key,slot_index',
    })
    .select(ANNUAL_TIMETABLE_SELECT);

  if (error) {
    throw new Error(error.message);
  }
  const savedRows = (data ?? []) as AnnualTimetableCell[];
  const workspaceId = savedRows[0]?.workspace_id ?? rows[0]?.workspace_id ?? '';
  if (workspaceId) {
    await notifySchoolProgramMutation(workspaceId, ['annualTimetable']);
    applyAnnualTimetablePostSaveInvalidation(savedRows);
  }
  return savedRows;
}
